import { BaseViewStore, DateTimeCellData } from 'core/stores';
import { ApplicationStore } from 'platform/application/stores';
import { action, makeObservable, observable } from 'mobx';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';
import UpdateInventoryStore from './UpdateInventoryStore';
import { ConfirmModalParams } from 'core/models';
import { isSeller, isSome, isSuperAdminOrganization } from 'core/utils';

class UpdateInventoryLogViewStore extends BaseViewStore {
	@observable change = false;
	isChannelInventoryLogPage = false;
	data = [];
	group = null;
	noDataItem = true;
	groupable = true;
	selectedInventoryForInventoryLog = null;
	@observable loadOlderLogsCount = 0;
	isUploadAllowed = false;

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		if (this.modalParams.data.store_path === 'channelEditViewStore') {
			this.isChannelInventoryLogPage = true;
			this.channel = modalParams.data.channel;
		} else {
			this.isChannelInventoryLogPage = false;
			this.product = modalParams.data.product;
			this.selectedInventoryForInventoryLog = this.modalParams.data.selectedInventoryForInventoryLog;
		}
		this.isUploadAllowed = isSeller(this.rootStore) || isSuperAdminOrganization(this.rootStore);
		this.updateInventoryStore = new UpdateInventoryStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (isSome(this.selectedInventoryForInventoryLog)) {
			const responseData = await this.updateInventoryStore.findUpdateInventoryLog({
				rpp: 30,
				page: 1,
				productId: this.product.id,
				warehouseId: this.selectedInventoryForInventoryLog.assignedWarehouseId,
				statuses: [2], // Status = Success
				embed: ['channel'],
				orderBy: 'dateUpdated',
				orderDirection: 'desc',
			});
			this.data = responseData.item;
			if (this.data != null && this.data.length > 0) {
				this.loadOlderLogs();
			}
		} else {
			this.tableData = this.defineTableData();
			if (this.isChannelInventoryLogPage) {
				await this.getAndPrepareChannelInventoryData();
			} else {
				await this.getAndPrepareProductInventoryLogData();
			}
		}

		this.resume();
	};

	async getAndPrepareChannelInventoryData() {
		const responseData = await this.updateInventoryStore.findGroupedInventoryLog({
			rpp: 400,
			page: 1,
			channelId: this.channel.id,
			embed: ['channel'],
		});

		this.data = [];
		let tempData = [];
		_.forEach(responseData, updateInventoryLog => {
			tempData.push({
				name: updateInventoryLog.name,
				sku: updateInventoryLog.sku,
				date: updateInventoryLog.dateCreated,
				qty: updateInventoryLog.quantity,
			});
		});
		this.group = { field: 'name' };
		tempData = _.orderBy(tempData, [obj => new Date(obj.date)], ['desc']);
		tempData = _.groupBy(tempData, 'name');
		this.data = _.map(tempData, (item, key) => {
			return {
				field: 'name',
				items: item,
				value: key,
				expanded: false,
			};
		});
	}

	async getAndPrepareProductInventoryLogData() {
		const responseData = await this.updateInventoryStore.findGroupedInventoryLog({
			rpp: 400,
			page: 1,
			productId: this.product.id,
			embed: ['channel'],
		});
		this.data = [];
		let tempData = [];
		_.forEach(responseData, updateInventoryLog => {
			tempData.push({
				channelName: updateInventoryLog.channel.name,
				name: updateInventoryLog.name,
				date: updateInventoryLog.dateCreated,
				qty: updateInventoryLog.quantity,
			});
		});
		this.group = { field: 'channelName' };
		tempData = _.orderBy(tempData, [obj => new Date(obj.date)], ['desc']);
		tempData = _.groupBy(tempData, 'channelName');
		this.data = _.map(tempData, (item, key) => {
			return {
				field: 'channelName',
				items: item,
				value: key,
				expanded: false,
			};
		});
	}

	@action.bound
	defineTableData() {
		return this.isChannelInventoryLogPage
			? {
					columns: [
						{
							key: 'sku',
							title: 'UPDATEINVENTORYLOG.SKU',
							selected: true,
							sortable: false,
							width: 150,
							filter: null,
						},
						{
							key: 'date',
							title: 'UPDATEINVENTORYLOG.LASTUPLOAD',
							dataCell: DateTimeCellData,
							dateFormat: 'LLL',
							useLocalTime: true,
							selected: true,
							sortable: false,
							width: 250,
							filter: null,
						},
						{
							key: 'qty',
							title: 'UPDATEINVENTORYLOG.QTY',
							selected: true,
							sortable: false,
							width: 100,
							filter: null,
						},
					],
			  }
			: {
					columns: [
						{
							key: 'name',
							title: 'UPDATEINVENTORYLOG.WAREHOUSEFORCHANNEL',
							selected: true,
							sortable: false,
							width: 200,
							filter: null,
						},
						{
							key: 'date',
							title: 'UPDATEINVENTORYLOG.LASTUPLOAD',
							dataCell: DateTimeCellData,
							dateFormat: 'LLL',
							useLocalTime: true,
							selected: true,
							sortable: false,
							width: 250,
							filter: null,
						},
						{
							key: 'qty',
							title: 'UPDATEINVENTORYLOG.QTY',
							selected: true,
							sortable: false,
							width: 100,
							filter: null,
						},
					],
			  };
	}

	@action.bound
	expandChange = evt => {
		evt.dataItem.expanded = !evt.dataItem.expanded;
		this.setAsChanged();
	};

	@action.bound
	setAsChanged = () => {
		this.change = !this.change;
	};

	@action.bound
	updateInventoryForChannel = async channelId => {
		this.suspend();
		try {
			const result = await this.updateInventoryStore.updateInventoryForChannel(
				channelId,
				sourceOfChange.channelEditUpdateInventoryLog
			);
			result
				? this.rootStore.notificationStore.success(['UPDATEINVENTORYLOG.SUCCESS', { name: this.channel.name }])
				: this.rootStore.notificationStore.error(['UPDATEINVENTORYLOG.NOTHINGTOUPLOAD', { name: this.channel.name }]);
			this.modalParams.success();
		} catch (error) {
			this.rootStore.notificationStore.error(['UPDATEINVENTORYLOG.ERROR', { name: this.channel.name }]);
		}
		this.resume();
	};

	@action.bound
	updateInventoryForProduct = async productId => {
		this.suspend();
		try {
			const result = await this.updateInventoryStore.updateInventoryForProduct(
				productId,
				sourceOfChange.productDetailUpdateInventoryLog
			);
			result
				? this.rootStore.notificationStore.success(['UPDATEINVENTORYLOG.SUCCESS', { name: this.product.mainSku }])
				: this.rootStore.notificationStore.error([
						'UPDATEINVENTORYLOG.NOTHINGTOUPLOAD',
						{ name: this.product.mainSku },
				  ]);
			this.modalParams.success();
		} catch (error) {
			this.rootStore.notificationStore.error(['UPDATEINVENTORYLOG.ERROR', { name: this.product.mainSku }]);
		}
		this.resume();
	};

	@action.bound
	openUploadInventoryConfirmModal() {
		this.isChannelInventoryLogPage
			? this.uploadInventoryConfirmModal.open({
					message: ['UPDATEINVENTORYLOG.CONFIRMCHANNELINVUPLOAD', { name: this.channel.name }],
			  })
			: this.uploadInventoryConfirmModal.open({
					message: ['UPDATEINVENTORYLOG.CONFIRMPRODUCTLINVUPLOAD', { name: this.product.mainSku }],
			  });
	}

	uploadInventoryConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			this.isChannelInventoryLogPage
				? await this.updateInventoryForChannel(this.channel.id)
				: await this.updateInventoryForProduct(this.product.id);
		},
	});

	@action.bound
	loadOlderLogs() {
		this.loadOlderLogsCount = this.loadOlderLogsCount + 3;
	}
}

export default UpdateInventoryLogViewStore;



// WEBPACK FOOTER //
// ./src/modules/product/stores/UpdateInventoryLogViewStore.js